import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";
import { KeyRound, Shield, ArrowLeft } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

const emailSchema = z.string().email("Invalid email address");
const passwordSchema = z.string().min(6, "Password must be at least 6 characters");

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // MFA state
  const [showMfa, setShowMfa] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // Check if user is site_admin
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'site_admin')
          .maybeSingle();

        if (roleData) {
          // Already logged in as site admin, redirect to dashboard
          navigate('/dashboard');
        } else {
          // Logged in but not site admin, sign out
          await supabase.auth.signOut();
          toast({
            title: "Access Denied",
            description: "Only site administrators can access the system during maintenance.",
            variant: "destructive",
          });
        }
      }
    };

    checkSession();
  }, [navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validate input
    try {
      emailSchema.parse(email);
      passwordSchema.parse(password);
    } catch (error: any) {
      if (error.errors) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
    }

    // Attempt sign in
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        title: "Login Failed",
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Check if MFA is required
    if (data.user) {
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
      
      if (factorsError) {
        console.error('Error checking MFA factors:', factorsError);
        setLoading(false);
        return;
      }

      const verifiedFactors = factorsData?.totp?.filter(f => f.status === 'verified') || [];
      
      if (verifiedFactors.length > 0) {
        // MFA is enabled - need to verify
        const factor = verifiedFactors[0];
        setMfaFactorId(factor.id);
        setPendingUserId(data.user.id);
        
        // Create MFA challenge
        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
          factorId: factor.id,
        });

        if (challengeError) {
          toast({
            title: "MFA Error",
            description: challengeError.message,
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        setMfaChallengeId(challengeData.id);
        setShowMfa(true);
        setLoading(false);
        return;
      }

      // No MFA - check if user is site_admin
      await checkSiteAdminAndProceed(data.user.id);
    }

    setLoading(false);
  };

  const checkSiteAdminAndProceed = async (userId: string) => {
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'site_admin')
      .maybeSingle();

    if (roleData) {
      // Site admin - grant access and set bypass flag
      sessionStorage.setItem('admin_maintenance_bypass', 'true');
      toast({
        title: "Access Granted",
        description: "Welcome back, administrator.",
      });
      navigate('/dashboard');
    } else {
      // Not a site admin - deny access
      sessionStorage.removeItem('admin_maintenance_bypass');
      await supabase.auth.signOut();
      toast({
        title: "Access Denied",
        description: "Only site administrators can access the system during maintenance.",
        variant: "destructive",
      });
    }
  };

  const handleMfaVerify = async () => {
    if (!mfaFactorId || otpCode.length !== 6) return;

    setVerifying(true);

    try {
      // Create a fresh challenge right before verification to avoid expiration
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId,
      });

      if (challengeError) {
        toast({
          title: "Verification Failed",
          description: challengeError.message,
          variant: "destructive",
        });
        setOtpCode("");
        setVerifying(false);
        return;
      }

      // Verify with the fresh challenge
      const { error } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challengeData.id,
        code: otpCode,
      });

      if (error) {
        toast({
          title: "Verification Failed",
          description: error.message,
          variant: "destructive",
        });
        setOtpCode("");
        setVerifying(false);
        return;
      }

      // MFA verified - check if user is site_admin
      if (pendingUserId) {
        await checkSiteAdminAndProceed(pendingUserId);
      }
    } catch (err: any) {
      toast({
        title: "Verification Failed",
        description: err.message || "An error occurred during verification",
        variant: "destructive",
      });
      setOtpCode("");
    }

    setVerifying(false);
  };

  const handleRecoveryCodeVerify = async () => {
    if (!recoveryCode.trim() || !pendingUserId) return;

    setVerifying(true);

    try {
      const { data, error } = await supabase.functions.invoke('mfa-recovery-codes', {
        body: {
          action: 'verify',
          code: recoveryCode.trim(),
        },
      });

      if (error || !data?.valid) {
        toast({
          title: "Invalid Recovery Code",
          description: data?.error || "The recovery code is invalid or has already been used.",
          variant: "destructive",
        });
        setRecoveryCode("");
        setVerifying(false);
        return;
      }

      // Recovery code valid — unenroll TOTP factors so AAL1 session can proceed
      const { error: bypassError } = await supabase.functions.invoke('mfa-recovery-bypass', {
        body: { userId: pendingUserId },
      });

      if (bypassError) {
        console.error('MFA bypass error:', bypassError);
        toast({
          title: "Recovery Failed",
          description: "Could not disable MFA. Please contact support.",
          variant: "destructive",
        });
        setVerifying(false);
        return;
      }

      toast({
        title: "MFA Disabled",
        description: "Two-factor authentication has been removed. Please re-enable it from settings.",
      });

      await checkSiteAdminAndProceed(pendingUserId);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to verify recovery code. Please try again.",
        variant: "destructive",
      });
    }

    setVerifying(false);
  };

  const resetMfaState = () => {
    setShowMfa(false);
    setMfaFactorId(null);
    setMfaChallengeId(null);
    setOtpCode("");
    setUseRecoveryCode(false);
    setRecoveryCode("");
    setPendingUserId(null);
  };

  // MFA Verification Screen
  if (showMfa) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-6">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-full bg-gray-100">
                <Shield className="w-8 h-8 text-gray-700" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Two-Factor Authentication
            </h1>
            <p className="text-sm text-gray-600">
              {useRecoveryCode 
                ? "Enter one of your saved recovery codes"
                : "Enter the code from your authenticator app"
              }
            </p>
          </div>

          {useRecoveryCode ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="recovery-code">Recovery Code</Label>
                <Input
                  id="recovery-code"
                  type="text"
                  placeholder="xxxxx-xxxxx"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  className="text-center font-mono"
                  autoFocus
                />
              </div>
              <Button
                onClick={handleRecoveryCodeVerify}
                className="w-full"
                disabled={verifying || !recoveryCode.trim()}
              >
                {verifying ? "Verifying..." : "Verify Recovery Code"}
              </Button>
              <Button
                variant="link"
                onClick={() => {
                  setUseRecoveryCode(false);
                  setRecoveryCode("");
                }}
                className="w-full text-sm text-gray-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to authenticator code
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={setOtpCode}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                onClick={handleMfaVerify}
                className="w-full"
                disabled={verifying || otpCode.length !== 6}
              >
                {verifying ? "Verifying..." : "Verify"}
              </Button>
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-500">Lost your device?</p>
                <Button
                  variant="link"
                  onClick={() => setUseRecoveryCode(true)}
                  className="text-sm"
                >
                  Use a recovery code instead
                </Button>
              </div>
            </div>
          )}

          <div className="text-center">
            <Button
              variant="link"
              onClick={resetMfaState}
              className="text-sm text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Login Form
  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-gray-100">
              <KeyRound className="w-8 h-8 text-gray-700" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Administrator Login
          </h1>
          <p className="text-sm text-gray-600">
            System is in maintenance mode
          </p>
        </div>

        <form onSubmit={handleSignIn} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input
              id="admin-email"
              type="email"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="admin-password">Password</Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <div className="text-center">
          <Button
            variant="link"
            onClick={() => navigate('/')}
            className="text-sm text-gray-600"
          >
            ← Back to maintenance page
          </Button>
        </div>
      </div>
    </div>
  );
}
